import React, { useEffect, useState } from 'react';
import azureDevOpsService from './services/azureDevOpsService';

export default function KpiSquadSocleFrontBugMetrics() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState('');
  const [bugDetails, setBugDetails] = useState({ created: [], closed: [] });

  const AREA_PATH = 'Fabrique\\Tribus\\Tribu Argonos\\Squads\\Squad Framework Front';

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      
      try {
        setProgress('Récupération des bugs créés et fermés...');
        
        const result = await azureDevOpsService.getBugsCreatedAndClosedByAreaPath(
          AREA_PATH,
          6 // 6 derniers mois
        );

        if (result.created.length === 0) {
          setProgress('Aucun bug trouvé dans les 6 derniers mois');
          setLoading(false);
          return;
        }

        setProgress('Calcul des métriques...');

        const createdCount = result.created.length;
        const closedCount = result.closed.length;
        const openCount = createdCount - closedCount;
        
        // Calculer les pourcentages
        const closureRate = createdCount > 0 ? ((closedCount / createdCount) * 100).toFixed(1) : 0;
        const openRate = createdCount > 0 ? ((openCount / createdCount) * 100).toFixed(1) : 0;

        // Grouper les bugs fermés par état
        const bugsByState = {};
        result.closed.forEach(bug => {
          if (!bugsByState[bug.state]) {
            bugsByState[bug.state] = [];
          }
          bugsByState[bug.state].push(bug);
        });

        // Grouper les bugs ouverts par état
        const openBugsByState = {};
        result.created
          .filter(bug => !['6- Done', '6- Cancelled', '5- Validation'].includes(bug.state))
          .forEach(bug => {
            if (!openBugsByState[bug.state]) {
              openBugsByState[bug.state] = [];
            }
            openBugsByState[bug.state].push(bug);
          });

        setMetrics({
          created: createdCount,
          closed: closedCount,
          open: openCount,
          closureRate: closureRate,
          openRate: openRate,
          byState: bugsByState,
          openByState: openBugsByState
        });

        // Formater les détails
        setBugDetails(result);
        setLoading(false);
      } catch (error) {
        console.error('Erreur récupération métriques bugs:', error);
        setProgress('Erreur lors de la récupération des données');
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  return (
    <div className="bg-white rounded shadow p-6">
      <h2 className="text-lg font-bold mb-4">Métriques Bugs - Squad Socle Front (6 derniers mois)</h2>
      
      {loading || !metrics ? (
        <div>
          <div className="text-gray-600">Chargement...</div>
          {progress && <div className="text-sm text-gray-500 mt-2">{progress}</div>}
        </div>
      ) : (
        <div>
          {/* Résumé des métriques */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 rounded p-4">
              <div className="text-sm text-gray-600 mb-1">Bugs créés</div>
              <div className="text-2xl font-bold text-blue-600">{metrics.created}</div>
              <div className="text-xs text-gray-500">6 derniers mois</div>
            </div>
            
            <div className="bg-green-50 rounded p-4">
              <div className="text-sm text-gray-600 mb-1">Bugs fermés</div>
              <div className="text-2xl font-bold text-green-600">{metrics.closed}</div>
              <div className="text-xs text-gray-500">{metrics.closureRate}%</div>
            </div>
            
            <div className="bg-orange-50 rounded p-4">
              <div className="text-sm text-gray-600 mb-1">Bugs ouverts</div>
              <div className="text-2xl font-bold text-orange-600">{metrics.open}</div>
              <div className="text-xs text-gray-500">{metrics.openRate}%</div>
            </div>
            
            <div className="bg-purple-50 rounded p-4">
              <div className="text-sm text-gray-600 mb-1">Ratio fermés/créés</div>
              <div className="text-2xl font-bold text-purple-600">{metrics.closureRate}%</div>
              <div className="text-xs text-gray-500">Taux de clôture</div>
            </div>
          </div>

          {/* Barre de progression visuelle */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-700">Progression de clôture</span>
              <span className="text-sm font-bold text-gray-600">{metrics.closureRate}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
              <div 
                className="bg-green-500 h-full transition-all duration-500"
                style={{ width: `${metrics.closureRate}%` }}
              ></div>
            </div>
          </div>

          {/* Détail des bugs fermés par état */}
          {Object.keys(metrics.byState).length > 0 && (
            <div className="mb-6">
              <h3 className="text-md font-semibold mb-3">Bugs fermés par état</h3>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                {Object.entries(metrics.byState).map(([state, bugs]) => (
                  <div key={state} className="border rounded p-3 bg-gray-50">
                    <div className="font-semibold text-gray-900">{state}</div>
                    <div className="text-2xl font-bold text-green-600 mt-1">{bugs.length}</div>
                    <div className="text-xs text-gray-500 mt-1">{((bugs.length / metrics.closed) * 100).toFixed(1)}% des fermés</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Détail des bugs ouverts par état */}
          {Object.keys(metrics.openByState).length > 0 && (
            <div className="mb-6">
              <h3 className="text-md font-semibold mb-3">Bugs ouverts par état</h3>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                {Object.entries(metrics.openByState).map(([state, bugs]) => (
                  <div key={state} className="border rounded p-3 bg-orange-50">
                    <div className="font-semibold text-gray-900">{state}</div>
                    <div className="text-2xl font-bold text-orange-600 mt-1">{bugs.length}</div>
                    <div className="text-xs text-gray-500 mt-1">{((bugs.length / metrics.open) * 100).toFixed(1)}% des ouverts</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tableau complet des bugs créés */}
          {bugDetails.created.length > 0 && (
            <div>
              <h3 className="text-md font-semibold mb-3">Tous les bugs créés ({bugDetails.created.length})</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-100 border-b">
                      <th className="px-4 py-2 text-left">ID</th>
                      <th className="px-4 py-2 text-left">Titre</th>
                      <th className="px-4 py-2 text-left">État</th>
                      <th className="px-4 py-2 text-center">Date création</th>
                      <th className="px-4 py-2 text-center">Date modification</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bugDetails.created.map((bug) => {
                      const isClosed = ['6- Done', '6- Cancelled', '5- Validation'].includes(bug.state);
                      return (
                        <tr 
                          key={bug.id} 
                          className={`border-b hover:bg-gray-50 ${isClosed ? 'bg-green-50' : 'bg-orange-50'}`}
                        >
                          <td className="px-4 py-2">
                            <a 
                              href={bug.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline font-medium"
                            >
                              #{bug.id}
                            </a>
                          </td>
                          <td className="px-4 py-2 max-w-xs truncate" title={bug.title}>{bug.title}</td>
                          <td className="px-4 py-2 text-sm">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              isClosed 
                                ? 'bg-green-200 text-green-800' 
                                : 'bg-orange-200 text-orange-800'
                            }`}>
                              {bug.state}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-center text-gray-600">
                            {new Date(bug.createdDate).toLocaleDateString('fr-FR')}
                          </td>
                          <td className="px-4 py-2 text-center text-gray-600">
                            {new Date(bug.changedDate).toLocaleDateString('fr-FR')}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
